import { useState, useEffect } from "react";
import { BsSearch } from "react-icons/bs";
import { CgBriefcase } from "react-icons/cg";
import { MdKeyboardArrowRight } from "react-icons/md";
import { format } from "date-fns";
import DateRangeSelector from "./DateRangeSelector";
import MultiFilter from "../Dashboard/MultiFilter";
import LineChart from "./LineChart";
import PieCharts, { GaugeChart } from "./PieCharts";
import {
  useFetchFiltersQuery,
  useFetchDashboardStatsMutation,
  useFetchFilterBasedOnBusinessUnitQuery,
} from "@services";
import { useTypedSelector } from "@/hooks";
function getSortedBusinessUnit(sortArray?: any[]) {
  if (!Array.isArray(sortArray)) return [];
  return [...sortArray]?.sort((a, b) => a?.label?.localeCompare(b?.label));
}

// Define the option type that matches what MultiFilter expects
type OptionType = {
  value: string;
  label: string;
};
function getSortedRegionOptions(sortArray?: any[]) {
  if (!Array.isArray(sortArray)) return [];
  return [...sortArray].sort((a, b) => a?.label?.localeCompare(b?.label));
}
function getSortedCountry(sortArray?: any[]) {
  if (!Array.isArray(sortArray)) return [];
  return [...sortArray]?.sort((a, b) => a?.label?.localeCompare(b?.label));
}
const Index = () => {
  const [businessUnit, setBusinessUnit] = useState<OptionType[]>([]);
  const [region, setRegion] = useState<OptionType[]>([]);
  const [country, setCountry] = useState<OptionType[]>([]);
  const [admin, setAdmin] = useState<OptionType[]>([]);
  const [companyJobFunctions, setCompanyJobFunctions] = useState<OptionType[]>(
    []
  );
  const [grade, setGrade] = useState<OptionType[]>([]);
  const [jobOfferStatus, setJobOfferStatus] = useState<OptionType[]>([]);
  const [showPicker, setShowPicker] = useState(false);

  const [range, setRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(null);

  const { user, permissions } = useTypedSelector(state => state.auth);
  const company_id = user?.company?.id || 0;
  const skip = !user?.company?.id;

  const {
    data: filterData,
    isLoading,
    error,
  } = useFetchFiltersQuery({ company_id }, { skip });

  const {
    data: filterBasedOnBusinessUnit,
    error: filterErrorBasedOnBusinessUnit,
    isLoading: filterLoadingBasedOnBusinessUnit,
    isSuccess: filterSuccessBasedOnBusinessUnit,
  } = useFetchFilterBasedOnBusinessUnitQuery({ company_id });

  const filteredData = filterBasedOnBusinessUnit?.data?.filter((item: any) =>
    businessUnit.some(bu => bu.label === item.name)
  );

  // const myregion = filteredData?.flatMap((d: any) => d.regions);
  const myregion = filteredData
    ?.flatMap((d: any) => d.regions)
    ?.filter(
      (value: any, index: number, self: any[]) =>
        index === self.findIndex((r: any) => r.id === value.id)
    );

  // Filter regions based on selected region labels
  // const myCountry = myregion?.filter((item: any) =>
  //   region.some(r => r.label === item.name)
  // );
  // const allCountries =
  //   myCountry?.flatMap((region: any) => region.countries) || [];
  const myCountry = myregion?.filter((item: any) =>
    region.some(r => r.value === item.id.toString())
  );

  const allCountries =
    myCountry?.flatMap((region: any) => region.countries) || [];

  const [fetchDashboardStats, { data: statsData }] =
    useFetchDashboardStatsMutation();

  useEffect(() => {
    if (company_id) {
      const body = {
        statuses: jobOfferStatus.map(item => item.value.toLowerCase()),
        company_job_functions: companyJobFunctions.map(item => item.value),
        countries: country.map(item => item.value),
        grade: grade.map(item => item.label),
        business_units: businessUnit.map(item => item.value),
        regions: region.map(item => item.value),
        evaluated_by: admin.map(item => item.label.toUpperCase()),
        startDate: range?.startDate,
        endDate: range?.endDate,
      };
      fetchDashboardStats({ company_id, body });
    }
  }, [
    company_id,
    jobOfferStatus,
    companyJobFunctions,
    country,
    grade,
    businessUnit,
    region,
    admin,
    range,
  ]);

  // Add global ResizeObserver error handler
  useEffect(() => {
    const errorHandler = (e: ErrorEvent) => {
      if (e.message.includes("ResizeObserver")) {
        e.preventDefault();
      }
    };
    window.addEventListener("error", errorHandler);
    return () => window.removeEventListener("error", errorHandler);
  }, []);

  const formatFullDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return format(date, "EEEE, do MMMM");
  };

  // Updated dummy data to match OptionType
  const dummyChartData: OptionType[] = [
    { value: "categoryA", label: "Category A" },
    { value: "categoryB", label: "Category B" },
    { value: "categoryC", label: "Category C" },
    { value: "categoryD", label: "Category D" },
  ];

  // For charts that expect {name, value} format
  const chartData = [
    { name: "Category A", value: 40 },
    { name: "Category B", value: 25 },
    { name: "Category C", value: 20 },
    { name: "Category D", value: 15 },
  ];

  const handleBusinessUnitChange = (selected: OptionType[]) => {
    setBusinessUnit(selected);
    setRegion([]); // 🔄 Clear region
    setCountry([]); // 🔄 Clear country
  };

  const handleRegionChange = (selected: OptionType[]) => {
    setRegion(selected);
    setCountry([]); // 🔄 Clear country
  };

  const keyMap: Record<string, string> = {
    open: "In-Progress",
    evaluated: "Evaluated",
    close: "Completed",
  };

  const statusOrder = ["open", "evaluated", "close"];
  const chartDataPi = statusOrder
    .map(key => {
      const value = Number(statsData?.data?.status?.[key]);

      return {
        name: keyMap?.[key],
        value,
      };
    })
    .filter(item => !isNaN(item.value) && item.value > 0);

  return (
    <div className="bg-white min-h-screen p-4 sm:p-5">
      <div className="top bg-bgSidebar px-5 py-3 rounded-lg flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        <div className="w-full lg:w-90 flex items-center rounded-md py-5 px-3 gap-x-3">
          <div
          // style={{ backgroundColor: "#A3C3F2" }}
          // className="w-16 px-2 py-2 rounded-full"
          >
            <CgBriefcase className="text-5xl text-white px-1" />
          </div>
          <div className="flex justify-between gap-x-2 items-center">
            <h1 className="text-white text-xl font-semibold">
              TOTAL JOB OFFERS
            </h1>
            <p className="text-white font-semibold text-3xl">
              {statsData?.data?.total}
            </p>
          </div>
        </div>

        <div className="w-full lg:w-80 ">
          <label className="dashboardLabel text-xs">Timeline</label>
          <button
            onClick={() => setShowPicker(true)}
            className="text-textColor flex items-center justify-between bg-white px-5 pb-3 rounded w-full lg:w-80"
          >
            <p className="mt-4 text-gray-700">
              {range?.startDate && range?.endDate ? (
                <>
                  {formatFullDate(range.startDate)} -{" "}
                  {formatFullDate(range.endDate)}
                </>
              ) : (
                <span>Select Date Range</span>
              )}
            </p>
            <p className="mt-4">
              <MdKeyboardArrowRight className="text-xl" />
            </p>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-5 bg-white px-5 py-6 rounded-md shadow grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 border border-gray-300">
        <MultiFilter
          label="Business Unit"
          options={getSortedBusinessUnit(
            filterData?.data?.business_units.map((d: any) => ({
              label: d.name,
              value: d.id,
            }))
          )}
          value={businessUnit}
          onChange={handleBusinessUnitChange}
        />

        <MultiFilter
          label="Region"
          disabled={businessUnit.length === 0}
          options={getSortedRegionOptions(
            myregion?.map((d: any) => ({
              value: d.id.toString(),
              label: d.name,
              countries: d.countries,
              business_units: d.business_units,
            }))
          )}
          value={region}
          onChange={handleRegionChange}
        />

        <MultiFilter
          label="Country"
          disabled={region.length === 0} // 🔒 Disable if no Region selected
          options={getSortedCountry(
            allCountries?.map((unit: any) => ({
              label: unit.name,
              value: unit.id,
            }))
          )}
          value={country}
          onChange={setCountry}
        />

        <MultiFilter
          label="Company Job Function"
          options={filterData?.data.company_job_functions.map((d: any) => ({
            label: d.name,
            value: d.id,
          }))}
          value={companyJobFunctions}
          onChange={setCompanyJobFunctions}
        />

        <MultiFilter
          label="Grade"
          options={filterData?.data.grades.map((grade: string) => ({
            label: grade,
            value: grade,
          }))}
          value={grade}
          onChange={setGrade}
        />

        <MultiFilter
          label="User"
          options={[{ value: "TOM", label: "Tom" }]}
          value={admin}
          onChange={setAdmin}
        />

        <MultiFilter
          label="Status"
          options={[
            { value: "evaluated", label: "Evaluated" },
            { value: "open", label: "In-Progress" },
            { value: "close", label: "Completed" },
          ]}
          value={jobOfferStatus}
          onChange={setJobOfferStatus}
        />
      </div>

      {/* Charts with debounce props */}
      {/* Debugging log */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6 grid-flow-dense">
        {Object.entries(statsData?.data?.business_unit || {}).length > 1 && (
          <LineChart
            title="Jobs by Business Unit"
            color="#2D7DEF"
            data={Object.entries(statsData?.data?.business_unit || {}).map(
              ([key, value]) => ({
                name: key,
                value: Number(value),
              })
            )}
          />
        )}

        {Object.entries(statsData?.data?.region || {}).length > 1 && (
          <LineChart
            title="Jobs by Region"
            color="#2D7DEF"
            data={Object.entries(statsData?.data?.region || {}).map(
              ([key, value]) => ({
                name: key,
                value: Number(value),
              })
            )}
          />
        )}

        {Object.entries(statsData?.data?.country || {}).length > 1 && (
          <LineChart
            title="Jobs by Country"
            color="#2D7DEF"
            data={Object.entries(statsData?.data?.country || {}).map(
              ([key, value]) => ({
                name: key,
                value: Number(value),
              })
            )}
          />
        )}

        {/* {Object.entries(statsData?.data?.company_job_function || {}).length > 1 && ( */}
        <LineChart
          title="Jobs by Company Job Function"
          color="#2D7DEF"
          data={Object.entries(statsData?.data?.company_job_function || {}).map(
            ([key, value]) => ({
              name: key,
              value: Number(value),
            })
          )}
        />
        {/* )} */}

        {/* {Object.entries(statsData?.data?.grade || {}).length > 1 && ( */}
        <div className="sm:col-span-1 xl:col-span-1">
          <LineChart
            title="Jobs by Grade"
            color="#2D7DEF"
            data={Object.entries(statsData?.data?.grade || {}).map(
              ([key, value]) => ({
                name: key,
                value: Number(value),
              })
            )}
          />
        </div>
        {/* )} */}

        {/* {Object.entries(statsData?.data?.evaluated_by || {}).length > 0 && ( */}
        <PieCharts
          title="Jobs by User"
          data={Object.entries(statsData?.data?.evaluated_by || {}).map(
            ([key, value]) => ({
              name: key,
              value: Number(value),
            })
          )}
          colordata={["FF6C23", "FFB60A"]}
        />
        {/*  )} */}

        {/* {chartDataPi.length > 0 && ( */}
        <PieCharts
          title="Jobs by Status"
          colordata={["4A3AFF", "FF6C23", "FEBB10"]}
          data={chartDataPi}
        />
        {/* )} */}
      </div>
      {showPicker && (
        <div className="fixed top-0 inset-0 z-50 bg-black bg-opacity-40 flex justify-center items-center">
          <DateRangeSelector
            initialDate={range ?? undefined} // 👈 safely convert null to undefined
            onApply={selected => {
              setRange(selected);
              setShowPicker(false);
            }}
            onCancel={() => setShowPicker(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Index;
